import { ViewFieldset } from '@payaca/ambrosia';
import Lightbox from '@payaca/components/lightbox/Lightbox';

import { CustomFieldHelperType, CustomFieldType } from '@payaca/custom-fields';
import { FC, useState } from 'react';

export type TProps<T extends CustomFieldType> = {
  type: T;
  definition: CustomFieldHelperType[T]['definition'];
  value?: CustomFieldHelperType[T]['value'] | null;
  customFormatter?: (
    value: CustomFieldHelperType[T]['value']
  ) => React.ReactNode;
};

const CustomFieldReadonly = <T extends CustomFieldType>(props: TProps<T>) => {
  const { definition, value, type } = props;

  let customFormatter: any = props.customFormatter || undefined;
  if (type === 'email') {
    customFormatter = (value: CustomFieldHelperType['email']['value']) => {
      return <a href={`mailto:${value}`}>{value}</a>;
    };
  }
  if (definition.type === 'url') {
    customFormatter = (value: CustomFieldHelperType['url']['value']) => {
      if (!value) return null;
      const isHttpUrl =
        value.url.startsWith('http://') || value.url.startsWith('https://');
      return isHttpUrl ? (
        <a target="_blank" rel="noreferrer" href={value.url}>
          {value.title || value.url}
        </a>
      ) : (
        <span>{value.title || value.url}</span>
      );
    };
  }
  if (definition.type === 'textarea') {
    customFormatter = (value: CustomFieldHelperType['textarea']['value']) => {
      if (!value) return null;
      return <div className="whitespace-pre-wrap">{value}</div>;
    };
  }
  if (definition.type === 'upload') {
    customFormatter = (value: CustomFieldHelperType['upload']['value']) => {
      if (!value || value.length === 0) return null;
      return <UploadsList value={value} />;
    };
  }

  return (
    <ViewFieldset.Field
      label={`${definition.label}:`}
      description={definition.description}
    >
      {definition.formatValue(value, customFormatter) || '-'}
    </ViewFieldset.Field>
  );
};

export default CustomFieldReadonly;

const UploadsList: FC<{ value: CustomFieldHelperType['upload']['value'] }> = ({
  value,
}) => {
  const allHaveThumbnail = value.every((upload) => upload.thumbnailUrl);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const hasNextSlide = lightboxIndex + 1 < value.length;
  const hasPrevSlide = lightboxIndex - 1 >= 0;

  return (
    <>
      <ul
        className={`list-none p-0 m-0 ${
          allHaveThumbnail ? 'flex gap-2 flex-wrap' : ''
        }`}
      >
        {value.map((upload, index) => {
          return (
            <li key={`${index}-${upload.url}`} className="cursor-pointer">
              {allHaveThumbnail ? (
                <button
                  className="p-0 border-0 bg-transparent cursor-pointer"
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                >
                  <img
                    src={upload.thumbnailUrl}
                    alt={upload.name}
                    className="block size-20 object-cover rounded-md"
                  />
                </button>
              ) : (
                upload.name
              )}
            </li>
          );
        })}
      </ul>

      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={
          hasNextSlide ? () => setLightboxIndex((prev) => prev + 1) : undefined
        }
        onPrev={
          hasPrevSlide ? () => setLightboxIndex((prev) => prev - 1) : undefined
        }
        lightboxDetail={{
          type: 'image',
          src: value[lightboxIndex].url,
          thumbnailSrc: value[lightboxIndex].thumbnailUrl,
          title: value[lightboxIndex].name,
          subtitle: null,
        }}
      />
    </>
  );
};
